import type { Maybe } from '@adornis/base/utilTypes.js';
import { extractIDOnlyFromFEKey } from './extractIDOnlyFromFEKey.js';
import { getRouteMinutesForRoute } from './getRoutePlanningWorkingDayStats.js';
import { getWorkingTimeMinutes } from './getWorkingTimeMinutes.js';

type FreeWorkingTimeEngineer = {
  id?: string | null;
  name?: string | null;
  startTime?: string | Date | null;
  endTime?: string | Date | null;
};

type FreeWorkingTimeRoute = {
  vehicleId?: string | null;
  vehicleName?: string | null;
  stops?: Array<{
    type?: string | null;
    ticketId?: string | null;
    travelDurationMinutes?: number | null;
    serviceDurationMinutes?: number | null;
    lat?: number | null;
    lng?: number | null;
  }> | null;
};

export type FreeAvailableWorkingTimeMinutes = {
  /** Free minutes across all available internal (non-partner) FEs, including unused ones. */
  allInternalFes: number;
  /** Free minutes only for internal FEs that have a route (used/assigned). */
  usedInternalFes: number;
};

/**
 * Free assignable working minutes for non-partner (internal) FEs.
 * Free = max(0, assignable working time - used route minutes (travel + service)).
 * FEs without a schedule are skipped (no available working time known).
 */
export function getFreeAvailableWorkingTimeMinutes(planning: {
  fieldEngineersAvailable?: Maybe<FreeWorkingTimeEngineer[]>;
  routes?: Maybe<FreeWorkingTimeRoute[]>;
}): FreeAvailableWorkingTimeMinutes {
  const routesByEngineerKey = new globalThis.Map<string, FreeWorkingTimeRoute>();
  for (const route of planning.routes ?? []) {
    for (const key of [route.vehicleId, route.vehicleName]) {
      const normalized = key ? extractIDOnlyFromFEKey(key) : '';
      if (normalized && !routesByEngineerKey.has(normalized)) {
        routesByEngineerKey.set(normalized, route);
      }
    }
  }

  let allInternalFes = 0;
  let usedInternalFes = 0;

  for (const engineer of planning.fieldEngineersAvailable ?? []) {
    const availableMinutes = getWorkingTimeMinutes(engineer.startTime, engineer.endTime);
    if (availableMinutes === null) continue;

    const idKey = engineer.id ? extractIDOnlyFromFEKey(engineer.id) : '';
    const nameKey = engineer.name ? extractIDOnlyFromFEKey(engineer.name) : '';
    const route =
      (idKey ? routesByEngineerKey.get(idKey) : undefined) ?? (nameKey ? routesByEngineerKey.get(nameKey) : undefined);
    const usedMinutes = getRouteMinutesForRoute(route);
    const freeMinutes = Math.max(0, availableMinutes - usedMinutes);

    allInternalFes += freeMinutes;
    if (route) usedInternalFes += freeMinutes;
  }

  return { allInternalFes, usedInternalFes };
}
